/**
 * Lianne Studio – Strickset Konfigurator
 *
 * Screens:
 * 1. studio-screen-start (Hero)
 * 2. studio-screen-inspiration (5-step flow)
 * 3. studio-screen-konfigurator (direct config)
 * 4. studio-screen-project (detail + cart)
 */

(function() {
  'use strict';

  // ─── DATA ────────────────────────────────────────────────────
  var DATA = {};
  var PRODUCTS = [];

  // Cache for fetched yarn products (by handle)
  var yarnProductCache = {};

  // ─── STATE ───────────────────────────────────────────────────
  var state = {
    moods: [],
    project: null,
    fiber: null,
    skill: null,
    selectedResult: null,
    prevScreen: 'studio-screen-inspiration',
    currentStep: 1
  };

  var configState = {
    project: null,
    projectKey: null,
    fiber: null,
    colors: [],
    skill: null
  };

  // Currently displayed project on detail page
  var currentProject = null;
  var currentSize = null;
  var currentOptions = {}; // { 0: 'S', 1: 'Regulär' } for multi-option products
  var currentColors = {}; // { optionIndex: { variantId, title, price, productTitle, quantityAvailable } }

  // ─── TOOLTIP DATA ──────────────────────────────────────────
  var TOOLTIPS = {
    Schurwolle: { term: 'Schurwolle', text: '"Schurwolle kommt vom lebenden Schaf — das Tier wird geschoren, nicht geschlachtet. Sie ist warm, elastisch und sehr langlebig. Der klassische Stoff für Pullover und Mützen."', tags: ['Tierische Faser', 'Nachwachsend', 'Warm', 'Atmungsaktiv'] },
    Merino: { term: 'Merinowolle', text: '"Merino ist eine besonders feine Schafswolle — viel weicher als normale Wolle und kaum kratzig. Perfekt wenn du empfindliche Haut hast."', tags: ['Tierische Faser', 'Sehr weich', 'Warm', 'Für empfindliche Haut'] },
    Alpaca: { term: 'Alpacawolle', text: '"Alpaca ist meine heimliche Lieblingswolle. Unglaublich leicht und dennoch wärmer als Schafwolle. Enthält kaum Lanolin."', tags: ['Tierische Faser', 'Sehr leicht', 'Hypoallergen', 'Luxuriös warm'] },
    Mohair: { term: 'Mohair', text: '"Mohair kommt von der Angoraziege und hat diesen unverwechselbaren flauschigen Glanz. Oft mit anderen Garnen kombiniert."', tags: ['Tierische Faser', 'Glänzend', 'Halo-Effekt', 'Oft kombiniert'] },
    Baumwolle: { term: 'Baumwolle', text: '"Baumwolle ist vollständig pflanzlich und damit vegan. Sehr hautfreundlich und gut waschbar. Perfekt für Sommer."', tags: ['Vegan', 'Hypoallergen', 'Maschinenwaschbar', 'Ideal für Sommer'] },
    Leinen: { term: 'Leinen', text: '"Leinen kommt aus der Flachspflanze. Sehr langlebig, wird mit jedem Waschen weicher."', tags: ['Vegan', 'Sehr langlebig', 'Kühlend', 'Wird weicher mit der Zeit'] },
    Bambus: { term: 'Bambusgarn', text: '"Bambus ist seidig weich, hat einen feinen Glanz und ist sehr atmungsaktiv. Besonders schön für Sommerkleidung."', tags: ['Vegan', 'Seidig weich', 'Atmungsaktiv', 'Nachhaltig'] },
    Superwash: { term: 'Superwash-Behandlung', text: '"Superwash bedeutet: diese Wolle kann in der Waschmaschine gewaschen werden — ohne einzulaufen."', tags: ['Maschinenwaschbar', 'Pflegeleicht', 'Läuft nicht ein'] },
    BabyMerino: { term: 'Baby Merino', text: '"Baby Merino ist die feinste Variante — so weich, dass sie für empfindliche Babyhaut geeignet ist."', tags: ['Extra weich', 'Für Babys', 'Hautfreundlich'] },
    Lauflaenge: { term: 'Lauflänge', text: '"Die Lauflänge sagt dir wie viele Meter Faden in einem Knäuel sind. Je länger, desto feiner das Garn."', tags: ['Wichtig für Mengenberechnung'] },
    FaserAllgemein: { term: 'Garn-Eigenschaften', text: '"Bei Lianne findest du Garne aus ganz unterschiedlichen Fasern — tierisch oder pflanzlich. Wenn du unsicher bist: schreib uns!"', tags: ['Tierisch: Wolle, Alpaca, Mohair', 'Pflanzlich: Baumwolle, Leinen, Bambus'] }
  };

  // ─── PROJECT TYPE DETECTION (title-based) ─────────────────
  var PROJECT_PATTERNS = {
    pullover: /pullover|sweater|pulli/i,
    cardigan: /cardigan|jacke|jacket/i,
    muetze: /mütze|muetze|beanie|hat|haube/i,
    schal: /schal|tuch|scarf|shawl|wrap|cowl/i,
    socken: /socken|socks|strümpfe/i,
    baby: /baby|kinder|child|kids/i
  };

  function detectProjectType(title) {
    for (var key in PROJECT_PATTERNS) {
      if (PROJECT_PATTERNS[key].test(title)) return key;
    }
    return 'sonstiges';
  }

  function detectDifficulty(title) {
    if (/anfänger|beginner|easy|einfach/i.test(title)) return 'anfaenger';
    if (/fortgeschritten|intermediate/i.test(title)) return 'mittel';
    if (/erfahren|advanced|profi/i.test(title)) return 'profi';
    return 'mittel'; // default
  }

  // ─── STOREFRONT API LOADER ──────────────────────────────────
  function loadProductsFromStorefront() {
    var query = [
      '{',
      '  products(first: 50, query: "product_type:Strickset") {',
      '    edges {',
      '      node {',
      '        id title handle vendor productType',
      '        priceRange { minVariantPrice { amount currencyCode } }',
      '        featuredImage { url }',
      '        images(first: 5) { edges { node { url } } }',
      '        constellations: metafield(namespace: "bundle", key: "constellations_new") { value }',
      '        globalOptions: metafield(namespace: "bundle", key: "global_options") { value }',
      '        externalLink: metafield(namespace: "custom", key: "external_link") { value }',
      '        bundleProductsRef: metafield(namespace: "bundle", key: "products") {',
      '          references(first: 20) {',
      '            nodes {',
      '              ... on Product {',
      '                id title handle',
      '                variants(first: 100) {',
      '                  nodes {',
      '                    id title availableForSale quantityAvailable',
      '                    price { amount currencyCode }',
      '                    image { url }',
      '                  }',
      '                }',
      '              }',
      '            }',
      '          }',
      '        }',
      '        multiBundleRef: metafield(namespace: "custom", key: "multi_bundle_products") {',
      '          reference {',
      '            ... on Metaobject {',
      '              fields { key value }',
      '            }',
      '          }',
      '        }',
      '      }',
      '    }',
      '  }',
      '}'
    ].join('\n');

    return fetch('https://' + DATA.shopDomain + '/api/2025-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': DATA.storefrontToken
      },
      body: JSON.stringify({ query: query })
    })
    .then(function(r) { return r.json(); })
    .then(function(response) {
      if (!response.data || !response.data.products) {
        console.error('Lianne Studio: Storefront API returned no products', response);
        return [];
      }

      return response.data.products.edges.map(function(edge) {
        var node = edge.node;
        var title = node.title;

        // Parse constellations metafield
        var constellations = null;
        if (node.constellations && node.constellations.value) {
          constellations = node.constellations.value;
        }

        // Parse globalOptions metafield
        var globalOptions = null;
        if (node.globalOptions && node.globalOptions.value) {
          globalOptions = node.globalOptions.value;
        }

        // Parse externalLink metafield
        var externalLink = null;
        if (node.externalLink && node.externalLink.value) {
          externalLink = node.externalLink.value;
        }

        // Parse bundle products from references
        var bundleProducts = [];
        if (node.bundleProductsRef && node.bundleProductsRef.references && node.bundleProductsRef.references.nodes) {
          bundleProducts = node.bundleProductsRef.references.nodes.map(function(bp) {
            // Cache each bundle product by handle
            var parsed = {
              id: bp.id,
              title: bp.title,
              handle: bp.handle,
              variants: bp.variants.nodes.map(function(v) {
                return {
                  id: v.id.replace('gid://shopify/ProductVariant/', ''),
                  gid: v.id,
                  title: v.title,
                  availableForSale: v.availableForSale,
                  quantityAvailable: v.quantityAvailable,
                  price: Math.round(parseFloat(v.price.amount) * 100),
                  image: v.image ? v.image.url : null
                };
              })
            };
            yarnProductCache[bp.handle] = parsed;
            return parsed;
          });
        }

        // Parse multi-bundle metaobject
        var multiBundleProducts = null;
        if (node.multiBundleRef && node.multiBundleRef.reference && node.multiBundleRef.reference.fields) {
          var fields = node.multiBundleRef.reference.fields;
          var mbData = {};
          fields.forEach(function(f) {
            mbData[f.key] = f.value;
          });
          multiBundleProducts = {
            optionNames: mbData.option_names || null,
            productHandles: mbData.product_handles || null,
            variantIgnoreList: mbData.variant_ignore_list || null
          };
        }

        return {
          id: node.id,
          title: title,
          handle: node.handle,
          url: '/products/' + node.handle,
          vendor: node.vendor,
          price: Math.round(parseFloat(node.priceRange.minVariantPrice.amount) * 100),
          image: node.featuredImage ? node.featuredImage.url : null,
          images: node.images.edges.map(function(e) { return e.node.url; }),
          projekttyp: detectProjectType(title),
          schwierigkeit: detectDifficulty(title),
          constellations: constellations,
          globalOptions: globalOptions,
          externalLink: externalLink,
          bundleProducts: bundleProducts,
          multiBundleProducts: multiBundleProducts
        };
      });
    })
    .catch(function(err) {
      console.error('Lianne Studio: Failed to load from Storefront API', err);
      return [];
    });
  }

  // ─── FETCH YARN PRODUCTS BY HANDLE ────────────────────────
  function fetchYarnProductsByHandles(handles) {
    // Filter out handles already cached
    var toFetch = handles.filter(function(h) { return !yarnProductCache[h]; });
    // Deduplicate
    var unique = [];
    toFetch.forEach(function(h) {
      if (unique.indexOf(h) === -1) unique.push(h);
    });

    if (unique.length === 0) {
      return Promise.resolve();
    }

    var queryParts = unique.map(function(handle, i) {
      return 'product' + i + ': productByHandle(handle: "' + handle + '") { id title handle variants(first: 100) { nodes { id title availableForSale quantityAvailable price { amount currencyCode } image { url } } } }';
    });

    var query = '{ ' + queryParts.join(' ') + ' }';

    return fetch('https://' + DATA.shopDomain + '/api/2025-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': DATA.storefrontToken
      },
      body: JSON.stringify({ query: query })
    })
    .then(function(r) { return r.json(); })
    .then(function(response) {
      if (!response.data) return;
      unique.forEach(function(handle, i) {
        var bp = response.data['product' + i];
        if (!bp) return;
        yarnProductCache[handle] = {
          id: bp.id,
          title: bp.title,
          handle: bp.handle,
          variants: bp.variants.nodes.map(function(v) {
            return {
              id: v.id.replace('gid://shopify/ProductVariant/', ''),
              gid: v.id,
              title: v.title,
              availableForSale: v.availableForSale,
              quantityAvailable: v.quantityAvailable,
              price: Math.round(parseFloat(v.price.amount) * 100),
              image: v.image ? v.image.url : null
            };
          })
        };
      });
    })
    .catch(function(err) {
      console.error('Lianne Studio: Failed to fetch yarn products', err);
    });
  }

  // ─── INIT ──────────────────────────────────────────────────
  function init() {
    var dataEl = document.getElementById('studio-product-data');
    if (!dataEl) return;

    try {
      DATA = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Lianne Studio: Failed to parse config data', e);
      return;
    }

    bindEvents();
    populateConfigProjectGrid();
    populateConfigFiberGrid();
    populateConfigSkillRow();

    // Load products from Storefront API
    loadProductsFromStorefront().then(function(products) {
      PRODUCTS = products;
      console.log('Lianne Studio: ' + products.length + ' Stricksets geladen');
    });
  }

  // ─── EVENT BINDING ───────────────────────────────────────────
  function bindEvents() {
    // Screen navigation
    document.querySelectorAll('[data-studio-action="show-screen"]').forEach(function(btn) {
      btn.addEventListener('click', function() { showScreen(btn.dataset.screen); });
    });

    // Step navigation
    document.querySelectorAll('[data-studio-action="next-step"]').forEach(function(btn) {
      btn.addEventListener('click', function() { nextStep(parseInt(btn.dataset.step)); });
    });

    // Mood cards (toggle)
    document.querySelectorAll('.studio-mood-card').forEach(function(card) {
      card.addEventListener('click', function() {
        card.classList.toggle('selected');
        var mood = card.dataset.mood;
        var idx = state.moods.indexOf(mood);
        if (idx > -1) state.moods.splice(idx, 1);
        else state.moods.push(mood);
      });
    });

    // Inspiration project cards
    document.querySelectorAll('#studio-project-grid .studio-project-card').forEach(function(card) {
      card.addEventListener('click', function() {
        document.querySelectorAll('#studio-project-grid .studio-project-card').forEach(function(c) { c.classList.remove('selected'); });
        card.classList.add('selected');
        state.project = card.dataset.project;
        var btn = document.getElementById('studio-btn-next-2');
        if (btn) btn.disabled = false;
      });
    });

    // Inspiration fiber cards
    document.querySelectorAll('#studio-fiber-grid .studio-fiber-card').forEach(function(card) {
      card.addEventListener('click', function() {
        document.querySelectorAll('#studio-fiber-grid .studio-fiber-card').forEach(function(c) { c.classList.remove('selected'); });
        card.classList.add('selected');
        state.fiber = card.dataset.fiber;
        var btn = document.getElementById('studio-btn-next-3');
        if (btn) btn.disabled = false;
      });
    });

    // Inspiration skill buttons
    document.querySelectorAll('#studio-skill-row .studio-skill-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        document.querySelectorAll('#studio-skill-row .studio-skill-btn').forEach(function(b) { b.classList.remove('selected'); });
        btn.classList.add('selected');
        state.skill = btn.dataset.skill;
        checkStep4();
      });
    });

    // Occasion buttons (toggle)
    document.querySelectorAll('#studio-occasion-row .studio-occasion-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        btn.classList.toggle('selected');
        checkStep4();
      });
    });

    // Tooltip triggers
    document.querySelectorAll('[data-tooltip]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        showTooltip(btn.dataset.tooltip);
      });
    });

    // Tooltip close
    var tooltipOverlay = document.getElementById('studio-tooltip-overlay');
    if (tooltipOverlay) {
      tooltipOverlay.addEventListener('click', function(e) {
        if (e.target === tooltipOverlay) closeTooltip();
      });
    }
    var tooltipClose = document.getElementById('studio-tooltip-close');
    if (tooltipClose) {
      tooltipClose.addEventListener('click', closeTooltip);
    }

    // Show project page from results
    var selectResultBtn = document.getElementById('studio-btn-select-result');
    if (selectResultBtn) {
      selectResultBtn.addEventListener('click', function() {
        if (state.selectedResult) {
          showProjectPage(state.selectedResult);
        }
      });
    }

    // Generate config result
    var configGenBtn = document.getElementById('studio-btn-config-generate');
    if (configGenBtn) {
      configGenBtn.addEventListener('click', generateConfigResult);
    }

    // Back from project
    var backFromProject = document.getElementById('studio-back-from-project');
    if (backFromProject) {
      backFromProject.addEventListener('click', function() { showScreen(state.prevScreen); });
    }

    // Add to cart
    var addToCartBtn = document.getElementById('studio-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', addToCart);
    }

    // Share
    var shareBtn = document.getElementById('studio-share-btn');
    if (shareBtn) {
      shareBtn.addEventListener('click', shareProject);
    }
  }

  // ─── SCREEN NAVIGATION ──────────────────────────────────────
  function showScreen(id) {
    document.querySelectorAll('.studio-screen').forEach(function(s) { s.classList.remove('active'); });
    var screen = document.getElementById(id);
    if (screen) {
      screen.classList.add('active');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // ─── STEP NAVIGATION ────────────────────────────────────────
  function nextStep(n) {
    var prevStep = document.getElementById('studio-insp-step-' + state.currentStep);
    if (prevStep) prevStep.style.display = 'none';

    state.currentStep = n;
    var nextStepEl = document.getElementById('studio-insp-step-' + n);
    if (nextStepEl) nextStepEl.style.display = 'block';

    var progressFill = document.getElementById('studio-progress-fill');
    if (progressFill) progressFill.style.width = (n / 5 * 100) + '%';

    if (n === 5) generateResults();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function checkStep4() {
    var hasSkill = state.skill !== null;
    var hasOccasion = document.querySelectorAll('#studio-occasion-row .studio-occasion-btn.selected').length > 0;
    var btn = document.getElementById('studio-btn-next-4');
    if (btn) btn.disabled = !(hasSkill && hasOccasion);
  }

  // ─── TOOLTIP ─────────────────────────────────────────────────
  function showTooltip(key) {
    var data = TOOLTIPS[key];
    if (!data) return;

    document.getElementById('studio-tt-term').textContent = data.term;
    document.getElementById('studio-tt-text').textContent = data.text;
    document.getElementById('studio-tt-tags').innerHTML = data.tags.map(function(t) {
      return '<span class="studio-tooltip-tag">' + t + '</span>';
    }).join('');

    document.getElementById('studio-tooltip-overlay').classList.add('show');
  }

  function closeTooltip() {
    document.getElementById('studio-tooltip-overlay').classList.remove('show');
  }

  // ─── RESULTS GENERATION ──────────────────────────────────────
  function generateResults() {
    var grid = document.getElementById('studio-results-grid');
    if (!grid) return;

    // Show loading
    grid.innerHTML = '<div class="studio-loading"><div class="studio-spinner"></div><span>Passende Stricksets werden geladen...</span></div>';

    // Filter products based on user selections
    var filtered = filterProducts(PRODUCTS, state);

    if (filtered.length === 0) {
      grid.innerHTML = '<div class="studio-loading"><span>Leider keine passenden Stricksets gefunden. Versuche andere Filter.</span></div>';
      return;
    }

    // Render result cards
    grid.innerHTML = '';
    filtered.slice(0, 6).forEach(function(product) {
      grid.innerHTML += renderResultCard(product);
    });

    // Bind click events on result cards
    grid.querySelectorAll('.studio-result-card').forEach(function(card) {
      card.addEventListener('click', function() {
        grid.querySelectorAll('.studio-result-card').forEach(function(c) { c.classList.remove('selected-result'); });
        card.classList.add('selected-result');
        state.selectedResult = card.dataset.handle;
        state.prevScreen = 'studio-screen-inspiration';
        var btn = document.getElementById('studio-btn-select-result');
        if (btn) btn.disabled = false;
      });
    });

    // Update summary text
    var summaryEl = document.getElementById('studio-result-summary-text');
    if (summaryEl) {
      var moodText = { herbst: 'herbstlicher Stimmung', frisch: 'frischer Stimmung', klassisch: 'klassischem Stil', mutig: 'mutigen Farben' };
      var moodStr = state.moods.length > 0 ? (moodText[state.moods[0]] || 'deiner Stimmung') : 'deiner Stimmung';
      summaryEl.textContent = 'Passend zu ' + moodStr + ' haben wir ' + filtered.length + ' Projekt' + (filtered.length !== 1 ? 'e' : '') + ' für dich gefunden.';
    }
  }

  function filterProducts(products, filters) {
    var result = products.slice();

    // Filter by project type
    if (filters.project) {
      var matching = result.filter(function(p) { return p.projekttyp === filters.project; });
      if (matching.length > 0) result = matching;
    }

    // Filter by mood
    if (filters.moods && filters.moods.length > 0) {
      var moodMatching = result.filter(function(p) {
        if (!p.stimmungen) return false;
        var prodMoods = p.stimmungen.split(',').map(function(m) { return m.trim(); });
        return filters.moods.some(function(m) { return prodMoods.indexOf(m) > -1; });
      });
      if (moodMatching.length > 0) result = moodMatching;
    }

    // Filter by difficulty
    if (filters.skill) {
      var skillMatching = result.filter(function(p) {
        if (!p.schwierigkeit) return true;
        if (filters.skill === 'anfaenger') return p.schwierigkeit === 'anfaenger';
        if (filters.skill === 'mittel') return ['anfaenger', 'mittel'].indexOf(p.schwierigkeit) > -1;
        return true; // profi can do anything
      });
      if (skillMatching.length > 0) result = skillMatching;
    }

    return result;
  }

  function renderResultCard(product) {
    var imgHtml = product.image
      ? '<div class="studio-result-img"><img src="' + product.image + '" alt="' + escapeHtml(product.title) + '" loading="lazy"></div>'
      : '<div class="studio-result-img-placeholder"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="18" rx="2"/></svg></div>';

    var price = formatMoney(product.price);

    return '<div class="studio-result-card" data-handle="' + product.handle + '">' +
      '<div class="studio-result-color-bar" style="background:var(--studio-green)"></div>' +
      imgHtml +
      '<div class="studio-result-body">' +
        '<div class="studio-result-project-type">' + escapeHtml(product.vendor) + '</div>' +
        '<div class="studio-result-title">' + escapeHtml(product.title) + '</div>' +
        '<div class="studio-result-meta">' +
          '<div class="studio-result-price">' + price + '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  // ─── PROJECT PAGE ────────────────────────────────────────────
  function showProjectPage(handleOrProduct) {
    var product;
    if (typeof handleOrProduct === 'string') {
      product = PRODUCTS.find(function(p) { return p.handle === handleOrProduct; });
    } else {
      product = handleOrProduct;
    }

    if (!product) return;
    currentProject = product;
    currentSize = null;
    currentOptions = {};
    currentColors = {};

    // Set basic info
    var titleEl = document.getElementById('studio-pp-title');
    if (titleEl) titleEl.textContent = product.title;

    var subtitleEl = document.getElementById('studio-pp-subtitle');
    if (subtitleEl) subtitleEl.textContent = product.vendor;

    var badgeEl = document.getElementById('studio-pp-badge');
    if (badgeEl) badgeEl.textContent = 'Lianne Strickset';

    // Hero image
    var heroEl = document.getElementById('studio-project-hero-bg');
    if (heroEl && product.image) {
      heroEl.innerHTML = '<img src="' + product.image + '" alt="' + escapeHtml(product.title) + '" loading="lazy">';
      heroEl.style.background = 'var(--studio-cream)';
    }

    // Lisa's tip
    var lisaTipEl = document.getElementById('studio-pp-lisa-tip');
    var lisaTipWrap = document.getElementById('studio-lisa-tip');
    if (product.lisaTipp && lisaTipEl && lisaTipWrap) {
      lisaTipEl.textContent = product.lisaTipp;
      lisaTipWrap.style.display = 'flex';
    } else if (lisaTipWrap) {
      lisaTipWrap.style.display = 'none';
    }

    // Check if we need to fetch multi-bundle yarn products first
    var multiBundleHandles = getMultiBundleHandles(product);
    if (multiBundleHandles.length > 0) {
      // Show loading state
      var colorContainer = document.getElementById('studio-color-selectors');
      if (colorContainer) {
        colorContainer.innerHTML = '<div class="studio-loading"><div class="studio-spinner"></div><span>Garne werden geladen...</span></div>';
      }

      fetchYarnProductsByHandles(multiBundleHandles).then(function() {
        renderProjectDetails(product);
      });
    } else {
      renderProjectDetails(product);
    }

    showScreen('studio-screen-project');
  }

  function renderProjectDetails(product) {
    // Parse constellations for size selector
    renderSizeSelector(product);

    // Render color selectors
    renderColorSelectors(product);

    // Render specs
    renderSpecs(product);

    // Update price
    updatePrice(product);

    // Enable/disable add to cart
    updateAddToCartState();
  }

  // ─── MULTI-BUNDLE HANDLE EXTRACTION ──────────────────────
  function getMultiBundleHandles(product) {
    if (!product.multiBundleProducts || !product.multiBundleProducts.productHandles) return [];

    var raw = product.multiBundleProducts.productHandles;
    // Handles are separated by " • "
    var handles = raw.split(' \u2022 ').map(function(h) { return h.trim(); }).filter(function(h) { return h; });
    return handles;
  }

  // ─── SIZE SELECTOR ───────────────────────────────────────────
  function renderSizeSelector(product) {
    var container = document.getElementById('studio-size-selector');
    if (!container) return;

    // Reset options state
    currentOptions = {};

    if (!product.constellations || !product.globalOptions) {
      container.style.display = 'none';
      return;
    }

    var globalOpts = parseGlobalOptions(product.globalOptions);
    if (!globalOpts.length) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';
    container.innerHTML = ''; // Clear all previous selectors

    // Render a selector block for EACH global option (size, machart, etc.)
    globalOpts.forEach(function(opt, optIndex) {
      var block = document.createElement('div');
      block.className = 'studio-config-block';

      // Label
      if (opt.label) {
        var label = document.createElement('div');
        label.className = 'studio-config-block-label';
        label.textContent = opt.label;
        block.appendChild(label);
      }

      // Option buttons
      var optionsRow = document.createElement('div');
      optionsRow.className = 'studio-size-options';
      optionsRow.dataset.optionIndex = optIndex;

      opt.values.forEach(function(val) {
        var btn = document.createElement('button');
        btn.className = 'studio-size-btn';
        btn.textContent = val;
        btn.dataset.value = val;
        btn.dataset.optionIndex = optIndex;
        btn.addEventListener('click', function() {
          optionsRow.querySelectorAll('.studio-size-btn').forEach(function(b) { b.classList.remove('selected'); });
          btn.classList.add('selected');
          currentOptions[optIndex] = val;
          // For backward compat, set currentSize to first option
          if (optIndex === 0) currentSize = val;
          // Re-render color selectors to update availability for new option
          renderColorSelectors(currentProject);
          updatePrice(currentProject);
          updateAddToCartState();
        });
        optionsRow.appendChild(btn);
      });

      block.appendChild(optionsRow);
      container.appendChild(block);

      // Auto-select first value
      var firstBtn = optionsRow.querySelector('.studio-size-btn');
      if (firstBtn) firstBtn.click();
    });
  }

  // ─── COLOR SELECTORS (Compact Cards + Overlay) ─────────────
  function renderColorSelectors(product) {
    var container = document.getElementById('studio-color-selectors');
    if (!container) return;
    container.innerHTML = '';

    var bundleProducts = getBundleProducts(product);
    if (!bundleProducts.length) return;

    // Get option names for labels
    var optionNames = [];
    if (product.multiBundleProducts && product.multiBundleProducts.optionNames) {
      optionNames = product.multiBundleProducts.optionNames.split(' \u2022 ').map(function(n) { return n.trim(); });
    }

    // Get quantities for current size
    var quantities = getQuantities(product, currentSize);

    bundleProducts.forEach(function(bp, index) {
      var label = optionNames[index] || bp.title;
      var requiredQty = quantities[index] || 1;

      // Auto-select first available variant if none selected
      if (!currentColors[index]) {
        var ignoreList = getVariantIgnoreList(product);
        var variants = bp.variants.filter(function(v) {
          return ignoreList.indexOf(v.title) === -1;
        });
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          var avail = v.quantityAvailable !== null
            ? (v.availableForSale && v.quantityAvailable >= requiredQty)
            : v.availableForSale;
          if (avail) {
            currentColors[index] = {
              variantId: v.id,
              title: v.title,
              price: v.price,
              image: v.image,
              productTitle: bp.title,
              productHandle: bp.handle,
              quantityAvailable: v.quantityAvailable
            };
            break;
          }
        }
      }

      var selectorDiv = document.createElement('div');
      selectorDiv.className = 'studio-color-selector';

      // Label
      selectorDiv.innerHTML = '<div class="studio-color-selector-label">' + escapeHtml(label) + '</div>';

      // Build compact card
      var color = currentColors[index];
      var card = document.createElement('div');
      card.className = 'studio-color-selected-card';
      card.dataset.slot = index;

      if (color) {
        // Selected color card
        var imgSrc = color.image || '';
        card.innerHTML =
          '<div class="studio-color-selected-image">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + escapeHtml(color.title) + '">' : '') +
          '</div>' +
          '<div class="studio-color-selected-info">' +
            '<div class="studio-color-selected-name">' + escapeHtml(color.title) + '</div>' +
            '<div class="studio-color-selected-product">' + escapeHtml(color.productTitle) + '</div>' +
            '<div class="studio-color-selected-qty">' + requiredQty + ' Kn\u00e4uel ben\u00f6tigt</div>' +
          '</div>' +
          '<div class="studio-color-selected-change">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
          '</div>';
      } else {
        // Empty placeholder card
        card.classList.add('studio-color-empty');
        card.innerHTML =
          '<div class="studio-color-empty-icon">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="15.5" r="2.5"/><circle cx="8.5" cy="15.5" r="2.5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/></svg>' +
          '</div>' +
          '<div class="studio-color-selected-info">' +
            '<div class="studio-color-selected-name">Farbe w\u00e4hlen</div>' +
            '<div class="studio-color-selected-product">' + escapeHtml(bp.title) + '</div>' +
          '</div>' +
          '<div class="studio-color-selected-change">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
          '</div>';
      }

      // Click to open overlay
      card.addEventListener('click', function() {
        openColorOverlay(index, product);
      });

      selectorDiv.appendChild(card);
      container.appendChild(selectorDiv);
    });

    // After rendering cards, update price and state
    updatePrice(product);
    updateAvailabilityStatus();
    updateAddToCartState();
  }

  function getVariantIgnoreList(product) {
    if (product.multiBundleProducts && product.multiBundleProducts.variantIgnoreList) {
      return product.multiBundleProducts.variantIgnoreList.split('\n').map(function(v) { return v.trim(); }).filter(function(v) { return v; });
    }
    return [];
  }

  // ─── COLOR OVERLAY ────────────────────────────────────────────
  function openColorOverlay(slotIndex, product) {
    var bundleProducts = getBundleProducts(product);
    var bp = bundleProducts[slotIndex];
    if (!bp) return;

    var optionNames = [];
    if (product.multiBundleProducts && product.multiBundleProducts.optionNames) {
      optionNames = product.multiBundleProducts.optionNames.split(' \u2022 ').map(function(n) { return n.trim(); });
    }
    var label = optionNames[slotIndex] || bp.title;
    var ignoreList = getVariantIgnoreList(product);
    var quantities = getQuantities(product, currentSize);
    var requiredQty = quantities[slotIndex] || 1;

    var variants = bp.variants.filter(function(v) {
      return ignoreList.indexOf(v.title) === -1;
    });

    // Build overlay HTML
    var selectedId = currentColors[slotIndex] ? currentColors[slotIndex].variantId : null;

    var gridHtml = '';
    variants.forEach(function(variant) {
      var isAvailable = variant.quantityAvailable !== null
        ? (variant.availableForSale && variant.quantityAvailable >= requiredQty)
        : variant.availableForSale;

      var classes = 'studio-color-overlay-item';
      if (variant.id === selectedId || variant.id.toString() === (selectedId || '').toString()) classes += ' selected';
      if (!isAvailable) classes += ' unavailable';

      var statusText = isAvailable ? 'Verf\u00fcgbar' : 'Nicht verf\u00fcgbar';

      gridHtml += '<div class="' + classes + '" data-variant-id="' + variant.id + '" data-slot="' + slotIndex + '">' +
        '<div class="studio-color-overlay-swatch">' +
          (variant.image ? '<img src="' + variant.image + '" alt="' + escapeHtml(variant.title) + '" loading="lazy">' : '') +
        '</div>' +
        '<div class="studio-color-overlay-name">' + escapeHtml(variant.title) + '</div>' +
        '<div class="studio-color-overlay-status">' + statusText + '</div>' +
      '</div>';
    });

    // Get or create overlay element
    var overlay = document.getElementById('studio-color-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'studio-color-overlay';
      overlay.id = 'studio-color-overlay';
      document.body.appendChild(overlay);
    }

    overlay.innerHTML =
      '<div class="studio-color-overlay-header">' +
        '<button class="studio-color-overlay-close" aria-label="Schlie\u00dfen">\u2715</button>' +
        '<h3>' + escapeHtml(label) + ' w\u00e4hlen</h3>' +
        '<div class="studio-color-overlay-product">' + escapeHtml(bp.title) + '</div>' +
      '</div>' +
      '<div class="studio-color-overlay-grid">' + gridHtml + '</div>';

    // Show overlay
    overlay.classList.add('show');
    document.body.style.overflow = 'hidden';

    // Bind close button
    overlay.querySelector('.studio-color-overlay-close').addEventListener('click', function() {
      closeColorOverlay();
    });

    // Bind clicking outside header+grid to close
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) closeColorOverlay();
    });

    // Bind variant clicks
    overlay.querySelectorAll('.studio-color-overlay-item').forEach(function(item) {
      item.addEventListener('click', function() {
        if (item.classList.contains('unavailable')) return;

        var variantId = item.dataset.variantId;
        var slot = parseInt(item.dataset.slot);

        // Find the variant data
        var selectedVariant = null;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id.toString() === variantId.toString()) {
            selectedVariant = variants[i];
            break;
          }
        }

        if (selectedVariant) {
          currentColors[slot] = {
            variantId: selectedVariant.id,
            title: selectedVariant.title,
            price: selectedVariant.price,
            image: selectedVariant.image,
            productTitle: bp.title,
            productHandle: bp.handle,
            quantityAvailable: selectedVariant.quantityAvailable
          };
        }

        closeColorOverlay();
        renderColorSelectors(product);
      });
    });

    // Scroll selected item into view
    var selectedItem = overlay.querySelector('.studio-color-overlay-item.selected');
    if (selectedItem) {
      setTimeout(function() {
        selectedItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);
    }
  }

  function closeColorOverlay() {
    var overlay = document.getElementById('studio-color-overlay');
    if (overlay) {
      overlay.classList.remove('show');
    }
    document.body.style.overflow = '';
  }

  // ─── SPECS ───────────────────────────────────────────────────
  function renderSpecs(product) {
    var container = document.getElementById('studio-project-specs');
    if (!container) return;

    var html = '';

    // Vendor
    html += '<div class="studio-spec-row">' +
      '<div class="studio-spec-label">Marke</div>' +
      '<div class="studio-spec-value">' + escapeHtml(product.vendor) + '</div>' +
    '</div>';

    // Bundle products info – show only yarn names, filter out instructions
    var bps = getBundleProducts(product);
    if (bps.length) {
      var uniqueYarnTitles = [];
      bps.forEach(function(bp) {
        var title = bp.title || '';
        // Skip instruction / pattern products
        var lower = title.toLowerCase();
        if (lower.indexOf('anleitung') > -1 || lower.indexOf('pattern') > -1 || lower.indexOf('instruction') > -1) return;
        if (uniqueYarnTitles.indexOf(title) === -1) uniqueYarnTitles.push(title);
      });
      if (uniqueYarnTitles.length) {
        html += '<div class="studio-spec-row">' +
          '<div class="studio-spec-label">Enthaltene Garne</div>' +
          '<div class="studio-spec-value">' + uniqueYarnTitles.map(function(t) { return escapeHtml(t); }).join(', ') + '</div>' +
        '</div>';
      }
    }

    // Sizes – extract clean values from globalOptions instead of raw constellations
    if (product.globalOptions) {
      var globalOpts = parseGlobalOptions(product.globalOptions);
      if (globalOpts.length > 0) {
        // Use the first option's values as sizes (e.g. "S, M, L")
        var sizeValues = globalOpts[0].values;
        if (sizeValues.length) {
          html += '<div class="studio-spec-row">' +
            '<div class="studio-spec-label">Gr\u00f6\u00dfen</div>' +
            '<div class="studio-spec-value">' + sizeValues.map(function(v) { return escapeHtml(v); }).join(', ') + '</div>' +
          '</div>';
        }
      }
    }

    container.innerHTML = html;
  }

  // ─── PRICE ───────────────────────────────────────────────────
  function updatePrice(product) {
    var container = document.getElementById('studio-price-summary');
    if (!container || !product) return;

    var totalPrice = 0;
    var priceRows = '';

    // Calculate quantities from constellation
    var quantities = getQuantities(product, currentSize);

    var colorEntries = Object.keys(currentColors);
    colorEntries.forEach(function(index) {
      var color = currentColors[index];
      var qty = quantities[parseInt(index)] || 1;
      var lineTotal = color.price * qty;
      totalPrice += lineTotal;
      priceRows += '<div class="studio-price-row">' +
        '<span>' + qty + '\u00d7 ' + escapeHtml(color.productTitle) + ' (' + escapeHtml(color.title) + ')</span>' +
        '<span>' + formatMoney(lineTotal) + '</span>' +
      '</div>';
    });

    priceRows += '<div class="studio-price-row total">' +
      '<span>Gesamt</span>' +
      '<span>' + formatMoney(totalPrice) + '</span>' +
    '</div>';

    container.innerHTML = priceRows;
  }

  // ─── AVAILABILITY STATUS ─────────────────────────────────────
  function updateAvailabilityStatus() {
    var container = document.getElementById('studio-availability-status');
    if (!container || !currentProject) {
      return;
    }

    var quantities = getQuantities(currentProject, currentSize);
    var warnings = [];

    Object.keys(currentColors).forEach(function(index) {
      var color = currentColors[index];
      var requiredQty = quantities[parseInt(index)] || 1;
      // Only warn if we actually have quantity data (not null)
      if (color.quantityAvailable !== null && color.quantityAvailable < requiredQty) {
        warnings.push(escapeHtml(color.title) + ' (' + escapeHtml(color.productTitle) + '): nur ' + color.quantityAvailable + ' verf\u00fcgbar, ' + requiredQty + ' ben\u00f6tigt');
      }
    });

    if (warnings.length > 0) {
      container.innerHTML = '<div class="studio-availability-warning">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
        '<div>' + warnings.join('<br>') + '</div>' +
      '</div>';
      container.style.display = 'block';
    } else {
      container.innerHTML = '';
      container.style.display = 'none';
    }
  }

  // ─── ADD TO CART ─────────────────────────────────────────────
  function addToCart() {
    var product = currentProject;
    if (!product) return;

    var btn = document.getElementById('studio-add-to-cart');
    if (!btn || btn.disabled) return;

    var bundleProducts = getBundleProducts(product);
    var quantities = getQuantities(product, currentSize);
    var bundleId = Date.now().toString();
    var items = [];

    for (var i = 0; i < bundleProducts.length; i++) {
      var color = currentColors[i];
      if (!color || !color.variantId) continue;

      // Strip gid:// prefix if present
      var numericId = color.variantId.toString().replace(/gid:\/\/shopify\/ProductVariant\//, '');

      items.push({
        id: parseInt(numericId),
        quantity: quantities[i] || 1,
        properties: {
          'Bundle': product.title,
          '_BundleID': bundleId
        }
      });
    }

    if (items.length === 0) {
      showToast('Bitte w\u00e4hle zuerst alle Farben aus.');
      return;
    }

    btn.disabled = true;
    btn.innerHTML = '<div class="studio-spinner" style="width:20px;height:20px;border-width:2px;"></div> Wird hinzugef\u00fcgt...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      showToast('Strickset wurde in den Warenkorb gelegt!');
      refreshCart();
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = getCartButtonHtml();
      }
    })
    .catch(function(err) {
      console.error('Cart error:', err);
      showToast('Fehler beim Hinzuf\u00fcgen. Bitte versuche es erneut.');
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = getCartButtonHtml();
      }
    });
  }

  function getCartButtonHtml() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg> Alles in den Warenkorb';
  }

  function refreshCart() {
    // Trigger the Be Yours theme's cart refresh events
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

    // Fetch cart data to update count badges
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update all cart count indicators
        document.querySelectorAll('.cart-count-bubble, [data-cart-count], .header__cart-count').forEach(function(el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.classList.remove('hidden');
            el.removeAttribute('hidden');
          }
        });

        // Be Yours theme: fetch section rendering to update cart drawer content
        var sectionId = document.querySelector('cart-drawer') ? 'cart-drawer' : 'mini-cart';
        return fetch('/?sections=' + sectionId);
      })
      .then(function(r) { return r.json(); })
      .then(function(sections) {
        // Update the cart drawer HTML from section rendering
        Object.keys(sections).forEach(function(key) {
          var target = document.getElementById(key);
          if (target && sections[key]) {
            var parsed = new DOMParser().parseFromString(sections[key], 'text/html');
            var newContent = parsed.getElementById(key);
            if (newContent) target.innerHTML = newContent.innerHTML;
          }
        });

        // Open the cart drawer
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          // Fallback: dispatch open event
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        }
      })
      .catch(function(err) {
        console.error('Lianne Studio: Cart refresh failed', err);
        // Fallback: try to open drawer anyway
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      });
  }

  // ─── CONFIGURATOR ────────────────────────────────────────────
  function populateConfigProjectGrid() {
    var grid = document.getElementById('studio-config-project-grid');
    if (!grid) return;
    // Clone from inspiration flow
    var sourceGrid = document.getElementById('studio-project-grid');
    if (sourceGrid) {
      grid.innerHTML = sourceGrid.innerHTML;
      grid.querySelectorAll('.studio-project-card').forEach(function(card) {
        card.classList.remove('selected');
        card.addEventListener('click', function() {
          grid.querySelectorAll('.studio-project-card').forEach(function(c) { c.classList.remove('selected'); });
          card.classList.add('selected');
          var nameEl = card.querySelector('.studio-project-name');
          configState.project = nameEl ? nameEl.textContent : '';
          configState.projectKey = card.dataset.project;
          updateConfigPreview('project', configState.project);
          checkConfig();
        });
      });
    }
  }

  function populateConfigFiberGrid() {
    var grid = document.getElementById('studio-config-fiber-grid');
    if (!grid) return;

    var fiberData = [
      { key: 'natuerlich', name: 'Nat\u00fcrliche Wolle', desc: 'Schurwolle, Alpaca, Mohair' },
      { key: 'vegan', name: 'Vegan & pflanzlich', desc: 'Baumwolle, Leinen, Bambus' },
      { key: 'weich', name: 'Extra weich', desc: 'Merino, Baby Merino, Alpaca' },
      { key: 'egal', name: 'Egal \u2014 Lisa w\u00e4hlt', desc: 'Beste Empfehlung f\u00fcr dein Projekt' }
    ];

    grid.innerHTML = fiberData.map(function(f) {
      return '<div class="studio-fiber-card" data-fiber="' + f.key + '">' +
        '<div class="studio-fiber-check"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>' +
        '<div class="studio-fiber-icon-wrap"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/></svg></div>' +
        '<div class="studio-fiber-name">' + f.name + '</div>' +
        '<div class="studio-fiber-desc">' + f.desc + '</div>' +
      '</div>';
    }).join('');

    var labels = { natuerlich: 'Nat\u00fcrliche Wolle', vegan: 'Vegan & pflanzlich', weich: 'Extra weich', egal: 'Lisa w\u00e4hlt' };
    grid.querySelectorAll('.studio-fiber-card').forEach(function(card) {
      card.addEventListener('click', function() {
        grid.querySelectorAll('.studio-fiber-card').forEach(function(c) { c.classList.remove('selected'); });
        card.classList.add('selected');
        configState.fiber = card.dataset.fiber;
        updateConfigPreview('fiber', labels[configState.fiber]);
        checkConfig();
      });
    });
  }

  function populateConfigSkillRow() {
    var row = document.getElementById('studio-config-skill-row');
    if (!row) return;

    var skills = [
      { key: 'Anf\u00e4nger:in', desc: 'Einfache Muster' },
      { key: 'Fortgeschritten', desc: 'Strukturmuster ok' },
      { key: 'Erfahren', desc: 'Alles m\u00f6glich' }
    ];

    row.innerHTML = skills.map(function(s) {
      return '<button class="studio-skill-btn" data-skill="' + s.key + '">' +
        '<div class="studio-skill-icon-wrap"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/></svg></div>' +
        '<div class="studio-skill-level">' + s.key + '</div>' +
        '<div class="studio-skill-desc">' + s.desc + '</div>' +
      '</button>';
    }).join('');

    row.querySelectorAll('.studio-skill-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        row.querySelectorAll('.studio-skill-btn').forEach(function(b) { b.classList.remove('selected'); });
        btn.classList.add('selected');
        configState.skill = btn.dataset.skill;
        updateConfigPreview('skill', configState.skill);
        checkConfig();
      });
    });
  }

  function updateConfigPreview(key, value) {
    var el = document.getElementById('studio-config-preview-' + key);
    if (el) {
      el.textContent = value || '';
      el.className = value ? 'studio-config-item-value' : 'studio-config-item-empty';
    }
  }

  function checkConfig() {
    var btn = document.getElementById('studio-btn-config-generate');
    if (btn) {
      btn.disabled = !(configState.projectKey && configState.fiber && configState.skill);
    }
  }

  function generateConfigResult() {
    // Find matching product
    var skillMap = { 'Anf\u00e4nger:in': 'anfaenger', 'Fortgeschritten': 'mittel', 'Erfahren': 'profi' };
    var matching = filterProducts(PRODUCTS, {
      project: configState.projectKey,
      fiber: configState.fiber,
      skill: skillMap[configState.skill] || 'profi'
    });

    if (matching.length === 0) matching = PRODUCTS;

    state.prevScreen = 'studio-screen-konfigurator';
    showProjectPage(matching[0]);
  }

  // ─── HELPERS ─────────────────────────────────────────────────
  function parseGlobalOptions(optStr) {
    if (!optStr) return [];
    // Supports two separator formats:
    // 1. "Größe:S,M,L::Label||Machart:Crop,Regular::Label" (|| separator)
    // 2. "Größe:S,M,L::Label\nLänge:Regulär,Croplänge,Überlänge::Label" (newline separator)
    var blocks;
    if (optStr.indexOf('||') > -1) {
      blocks = optStr.split('||');
    } else {
      blocks = optStr.split('\n');
    }
    return blocks.filter(function(b) { return b.trim(); }).map(function(block) {
      var parts = block.split('::');
      var nameValues = parts[0].split(':');
      return {
        name: (nameValues[0] || '').trim(),
        values: nameValues[1] ? nameValues[1].split(',').map(function(v) { return v.trim(); }) : [],
        label: (parts[1] || '').trim()
      };
    });
  }

  function getQuantities(product, size) {
    if (!product || !product.constellations) return {};

    // Build the constellation key from all selected options
    // Format 1 (single option): "Klein_2_1" → key = "Klein"
    // Format 2 (multi option): "S:Regulär_8" → key = "S:Regulär"
    var constellationKey = '';
    var globalOpts = parseGlobalOptions(product.globalOptions);

    if (globalOpts.length <= 1) {
      // Single option (size only)
      constellationKey = currentOptions[0] || size || '';
    } else {
      // Multi option: combine with colon, e.g. "S:Regulär"
      var parts = [];
      for (var i = 0; i < globalOpts.length; i++) {
        if (currentOptions[i]) parts.push(currentOptions[i]);
      }
      constellationKey = parts.join(':');
    }

    if (!constellationKey) return {};

    var lines = product.constellations.split('\n').filter(function(l) { return l.trim(); });
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      var underscoreIdx = line.indexOf('_');
      if (underscoreIdx === -1) continue;
      var lineKey = line.substring(0, underscoreIdx);
      var qtyParts = line.substring(underscoreIdx + 1).split('_');

      if (lineKey === constellationKey) {
        var quantities = {};
        for (var j = 0; j < qtyParts.length; j++) {
          quantities[j] = parseInt(qtyParts[j]) || 1;
        }
        return quantities;
      }
    }

    // Default: 1 per product
    return {};
  }

  function getBundleProducts(product) {
    // Single bundle: products are directly attached via bundle.products metafield
    if (product.bundleProducts && product.bundleProducts.length > 0) {
      return product.bundleProducts;
    }

    // Multi bundle: product handles come from the metaobject, resolved from cache
    if (product.multiBundleProducts && product.multiBundleProducts.productHandles) {
      var handles = getMultiBundleHandles(product);
      var results = [];
      handles.forEach(function(handle) {
        var cached = yarnProductCache[handle];
        if (cached) {
          results.push(cached);
        }
      });
      return results;
    }

    return [];
  }

  function formatMoney(cents) {
    // Shopify prices are in cents
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    return '\u20ac' + amount;
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function showToast(msg) {
    var toast = document.getElementById('studio-toast');
    var msgEl = document.getElementById('studio-toast-msg');
    if (!toast || !msgEl) return;
    msgEl.textContent = msg;
    toast.classList.add('show');
    setTimeout(function() { toast.classList.remove('show'); }, 3000);
  }

  function shareProject() {
    if (navigator.share && currentProject) {
      navigator.share({
        title: currentProject.title + ' \u2013 Lianne Studio',
        url: window.location.href
      }).catch(function() {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(function() {
        showToast('Link wurde kopiert!');
      }).catch(function() {
        showToast('Link konnte nicht kopiert werden.');
      });
    }
  }

  function updateAddToCartState() {
    var btn = document.getElementById('studio-add-to-cart');
    if (!btn) return;

    var bundleProducts = getBundleProducts(currentProject);
    var allColorsSelected = bundleProducts.length === 0 ||
      bundleProducts.every(function(_, i) { return currentColors[i]; });

    var hasSizeIfNeeded = !currentProject || !currentProject.constellations || currentSize;

    // Also check availability of all selected colors
    var allAvailable = true;
    if (currentProject && currentSize) {
      var quantities = getQuantities(currentProject, currentSize);
      Object.keys(currentColors).forEach(function(index) {
        var color = currentColors[index];
        var requiredQty = quantities[parseInt(index)] || 1;
        if (color.quantityAvailable !== null && color.quantityAvailable < requiredQty) {
          allAvailable = false;
        }
      });
    }

    btn.disabled = !(allColorsSelected && hasSizeIfNeeded && allAvailable);
  }

  // ─── BOOT ────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
